/**
 * Build Your Bundle / Mix & Match component (Minimog theme).
 *
 * Customer flow: pick an offer tier (Step 1) -> fill that many product
 * "slots" via a compact select-style picker, with variant selection where
 * needed (Step 2) -> add every selected variant to the cart in a single
 * batched request, tagged with a shared `_bundle_id` line property.
 *
 * The percentage discount and free shipping shown here are an ESTIMATE for
 * UX purposes only. The number that actually applies at checkout is
 * computed server-side by the `bundle-builder-discount` Shopify Function,
 * which independently re-groups cart lines by `_bundle_id`, re-validates
 * collection membership, and looks the tier up from the
 * `custom.bundle_tiers` shop metafield — this file cannot influence that
 * calculation, by design.
 */
if (!customElements.get('m-bundle-builder')) {
  class MBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.offer = null;
      // One entry per bundle slot: null (empty) or
      // { productId, variantId, title, variantTitle, image, priceCents, priceFormatted }
      this.slots = [];
      this.products = this.parseProductsData();
      this.allowDuplicates = this.dataset.allowDuplicates === 'true';

      this.activeSlotIndex = null;
      this.activeTriggerEl = null;
      this.pickerSearchTerm = '';
      this.pickerExpandedProductId = null;
      this.pickerOptionSelections = {};

      this.messageTimeout = null;

      this.messageBox = this.querySelector('[data-role="message"]');
      this.step2 = this.querySelector('[data-role="step2"]');
      this.step2Sub = this.querySelector('[data-role="step2-sub"]');
      this.slotsContainer = this.querySelector('[data-role="slots"]');
      this.progressText = this.querySelector('[data-role="progress-text"]');
      this.progressBarFill = this.querySelector('[data-role="progress-bar-fill"]');
      this.summaryList = this.querySelector('[data-role="summary-list"]');
      this.summaryCount = this.querySelector('[data-role="summary-count"]');
      this.summarySubtotal = this.querySelector('[data-role="summary-subtotal"]');
      this.summaryDiscount = this.querySelector('[data-role="summary-discount"]');
      this.summaryTotal = this.querySelector('[data-role="summary-total"]');
      this.summaryShipping = this.querySelector('[data-role="summary-shipping"]');
      this.summaryCta = this.querySelector('[data-role="summary-cta"]');
      this.stickyBar = this.querySelector('[data-role="sticky-bar"]');
      this.stickyProgressText = this.querySelector('[data-role="sticky-progress-text"]');
      this.stickyCta = this.querySelector('[data-role="sticky-cta"]');

      this.pickerOverlay = this.querySelector('[data-role="picker-overlay"]');
      this.pickerTitle = this.querySelector('[data-role="picker-title"]');
      this.pickerSearchInput = this.querySelector('[data-role="picker-search"]');
      this.pickerList = this.querySelector('[data-role="picker-list"]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      if (this.pickerOverlay) {
        this.pickerOverlay.addEventListener('mousedown', (event) => {
          if (event.target === this.pickerOverlay) this.closePicker();
        });
      }

      this.trackEvent('bundle_viewed');
    }

    parseProductsData() {
      const script = this.querySelector('[data-role="products-json"]');
      if (!script) return [];
      try {
        const data = JSON.parse(script.textContent);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Bundle builder: failed to parse product data', error);
        return [];
      }
    }

    // ---------------------------------------------------------------------
    // Event delegation
    // ---------------------------------------------------------------------

    onClick(event) {
      const offerCard = event.target.closest('[data-role="offer-card"]');
      if (offerCard && this.contains(offerCard)) {
        this.selectOffer(offerCard);
        return;
      }

      const pickerClose = event.target.closest('[data-role="picker-close"]');
      if (pickerClose && this.contains(pickerClose)) {
        this.closePicker();
        return;
      }

      const slotChange = event.target.closest('[data-role="slot-change"]');
      if (slotChange && this.contains(slotChange)) {
        event.preventDefault();
        this.openPicker(Number(slotChange.dataset.slotIndex), slotChange);
        return;
      }

      const slotTrigger = event.target.closest('[data-role="slot-trigger"]');
      if (slotTrigger && this.contains(slotTrigger)) {
        this.openPicker(Number(slotTrigger.dataset.slotIndex), slotTrigger);
        return;
      }

      const pickerRowMain = event.target.closest('[data-role="picker-row-main"]');
      if (pickerRowMain && this.contains(pickerRowMain)) {
        this.onPickerRowClick(pickerRowMain);
        return;
      }

      const pickerConfirm = event.target.closest('[data-role="picker-confirm"]');
      if (pickerConfirm && this.contains(pickerConfirm)) {
        this.onPickerConfirm(pickerConfirm);
        return;
      }

      const removeBtn = event.target.closest('[data-role="remove-product"]');
      if (removeBtn && this.contains(removeBtn)) {
        this.removeFromSlot(Number(removeBtn.dataset.slotIndex));
        return;
      }

      if (event.target.closest('[data-role="summary-cta"], [data-role="sticky-cta"]')) {
        this.addBundleToCart();
      }
    }

    onChange(event) {
      const optionSelect = event.target.closest('[data-role="picker-option-select"]');
      if (optionSelect && this.contains(optionSelect)) {
        this.onPickerOptionChange(optionSelect);
      }
    }

    onInput(event) {
      const search = event.target.closest('[data-role="picker-search"]');
      if (search && this.contains(search)) {
        this.pickerSearchTerm = search.value.trim().toLowerCase();
        this.renderPickerList();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.pickerOverlay && !this.pickerOverlay.hidden) {
        this.closePicker();
      }
    }

    // ---------------------------------------------------------------------
    // Offer / slot lifecycle
    // ---------------------------------------------------------------------

    selectOffer(card) {
      const quantity = Number(card.dataset.quantity);
      const discount = Number(card.dataset.discount);
      const freeShipping = card.dataset.freeShipping === 'true';
      const previousQuantity = this.offer ? this.offer.quantity : undefined;

      this.offer = { id: card.dataset.offerId, quantity, discount, freeShipping };

      this.querySelectorAll('[data-role="offer-card"]').forEach((other) => {
        other.classList.toggle('is-active', other === card);
        other.setAttribute('aria-pressed', String(other === card));
      });

      if (this.slots.length > quantity) {
        const removedFilled = this.slots.slice(quantity).filter(Boolean).length;
        this.slots = this.slots.slice(0, quantity);
        if (removedFilled > 0) {
          this.showMessage(`Bundle updated to ${quantity} pieces — extra selections were removed.`);
        } else if (previousQuantity !== undefined && previousQuantity !== quantity) {
          this.showMessage(`Bundle updated to ${quantity} pieces.`);
        }
      } else if (this.slots.length < quantity) {
        this.slots = this.slots.concat(new Array(quantity - this.slots.length).fill(null));
        if (previousQuantity !== undefined && previousQuantity !== quantity) {
          this.showMessage(`Bundle updated to ${quantity} pieces.`);
        }
      }

      if (this.step2) this.step2.hidden = false;
      if (this.step2Sub) this.step2Sub.textContent = `Choose ${quantity} product${quantity === 1 ? '' : 's'}`;

      this.trackEvent('bundle_offer_selected', { quantity, discount, freeShipping });
      this.renderSlots();
      this.render();
      this.step2 && this.step2.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // ---------------------------------------------------------------------
    // Slot rendering
    // ---------------------------------------------------------------------

    renderSlots() {
      if (!this.slotsContainer || !this.offer) return;
      const html = this.slots.map((entry, index) => this.buildSlotHtml(entry, index)).join('');
      this.slotsContainer.innerHTML = html;
    }

    buildSlotHtml(entry, index) {
      const label = `<p class="bundle-slot__label">Product ${index + 1}</p>`;

      if (!entry) {
        return `
          <div class="bundle-slot" data-role="slot" data-slot-index="${index}">
            ${label}
            <button type="button" class="bundle-slot__trigger" data-role="slot-trigger" data-slot-index="${index}" aria-haspopup="dialog">
              <span class="bundle-slot__placeholder">Select a product</span>
              <span class="bundle-slot__chevron" aria-hidden="true">&#9662;</span>
            </button>
          </div>
        `;
      }

      return `
        <div class="bundle-slot" data-role="slot" data-slot-index="${index}">
          ${label}
          <div class="bundle-slot__trigger is-filled" data-role="slot-trigger" data-slot-index="${index}">
            <img class="bundle-slot__thumb" src="${escapeAttr(entry.image)}" alt="" width="44" height="44" loading="lazy">
            <span class="bundle-slot__info">
              <span class="bundle-slot__title">${escapeHtml(entry.title)}</span>
              ${entry.variantTitle ? `<span class="bundle-slot__variant">${escapeHtml(entry.variantTitle)}</span>` : ''}
              <span class="bundle-slot__price">${escapeHtml(entry.priceFormatted)}</span>
            </span>
            <span class="bundle-slot__check" aria-hidden="true">&#10003;</span>
            <button type="button" class="bundle-slot__change" data-role="slot-change" data-slot-index="${index}">Change</button>
          </div>
        </div>
      `;
    }

    // ---------------------------------------------------------------------
    // Picker overlay
    // ---------------------------------------------------------------------

    openPicker(slotIndex, triggerEl) {
      if (Number.isNaN(slotIndex) || !this.pickerOverlay) return;
      this.activeSlotIndex = slotIndex;
      this.activeTriggerEl = triggerEl || null;
      this.pickerSearchTerm = '';
      this.pickerExpandedProductId = null;
      this.pickerOptionSelections = {};

      const entry = this.slots[slotIndex];
      if (entry) {
        const product = this.products.find((p) => String(p.id) === String(entry.productId));
        if (product && !product.hasOnlyDefaultVariant) {
          this.pickerExpandedProductId = String(product.id);
          this.pickerOptionSelections[product.id] = this.getSelectionsForVariant(product, entry.variantId);
        }
      }

      if (this.pickerSearchInput) this.pickerSearchInput.value = '';
      if (this.pickerTitle) this.pickerTitle.textContent = `Select — Product ${slotIndex + 1}`;

      this.pickerOverlay.hidden = false;
      document.documentElement.classList.add('bundle-picker-open');
      this.renderPickerList();

      window.requestAnimationFrame(() => {
        if (this.pickerSearchInput) this.pickerSearchInput.focus({ preventScroll: true });
      });

      this.trackEvent('bundle_picker_opened', { slotIndex });
    }

    closePicker() {
      if (!this.pickerOverlay) return;
      this.pickerOverlay.hidden = true;
      document.documentElement.classList.remove('bundle-picker-open');
      const trigger = this.activeTriggerEl;
      this.activeSlotIndex = null;
      this.activeTriggerEl = null;
      this.pickerExpandedProductId = null;
      this.pickerOptionSelections = {};
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    }

    usedVariantIds(excludeSlotIndex) {
      const ids = new Set();
      this.slots.forEach((entry, index) => {
        if (!entry || index === excludeSlotIndex) return;
        ids.add(String(entry.variantId));
      });
      return ids;
    }

    renderPickerList() {
      if (!this.pickerList) return;
      const term = this.pickerSearchTerm;
      const products = this.products.filter((product) => !term || product.title.toLowerCase().includes(term));

      if (products.length === 0) {
        this.pickerList.innerHTML = '<p class="bundle-picker__empty">No products match your search.</p>';
        return;
      }

      const used = this.allowDuplicates ? new Set() : this.usedVariantIds(this.activeSlotIndex);
      const currentEntry = this.activeSlotIndex != null ? this.slots[this.activeSlotIndex] : null;

      this.pickerList.innerHTML = products.map((product) => this.buildPickerRowHtml(product, used, currentEntry)).join('');
    }

    buildPickerRowHtml(product, usedVariantIds, currentEntry) {
      const isCurrentProduct = currentEntry && String(currentEntry.productId) === String(product.id);

      if (product.hasOnlyDefaultVariant) {
        const variant = product.variants[0];
        const isUsedElsewhere = variant && usedVariantIds.has(String(variant.id)) && !isCurrentProduct;
        const isSelected = isCurrentProduct && currentEntry && String(currentEntry.variantId) === String(variant.id);
        const unavailable = !product.available || !variant || !variant.available;
        const disabled = unavailable || isUsedElsewhere;

        let meta = '';
        if (unavailable) meta = 'Sold out';
        else if (isUsedElsewhere) meta = 'Already in your bundle';
        else if (isSelected) meta = 'Selected';

        return `
          <div class="bundle-picker__row${disabled ? ' is-disabled' : ''}${isSelected ? ' is-selected' : ''}" data-role="picker-row">
            <button
              type="button"
              class="bundle-picker__row-main"
              data-role="picker-row-main"
              data-product-id="${product.id}"
              data-simple="true"
              ${disabled ? 'disabled aria-disabled="true"' : ''}
            >
              <img class="bundle-picker__thumb" src="${escapeAttr(product.image)}" alt="" width="44" height="44" loading="lazy">
              <span class="bundle-picker__row-info">
                <span class="bundle-picker__row-title">${escapeHtml(product.title)}</span>
                ${meta ? `<span class="bundle-picker__row-meta">${escapeHtml(meta)}</span>` : ''}
              </span>
              <span class="bundle-picker__row-price">${escapeHtml(variant ? variant.priceFormatted : '')}</span>
              <span class="bundle-picker__row-arrow" aria-hidden="true">${isSelected ? '&#10003;' : '&#8250;'}</span>
            </button>
          </div>
        `;
      }

      // Variant-bearing product: expandable row with option selects.
      const isExpanded = this.pickerExpandedProductId === String(product.id);
      const availableVariants = product.variants.filter((v) => v.available && (this.allowDuplicates || !usedVariantIds.has(String(v.id)) || (isCurrentProduct && String(currentEntry.variantId) === String(v.id))));
      const allSoldOutOrUsed = availableVariants.length === 0;
      const prices = product.variants.map((v) => Number(v.price));
      const minCents = Math.min(...prices);
      const maxCents = Math.max(...prices);
      const priceLabel = minCents === maxCents ? this.formatMoney(minCents) : `${this.formatMoney(minCents)} – ${this.formatMoney(maxCents)}`;

      let meta = '';
      if (!product.available) meta = 'Sold out';
      else if (allSoldOutOrUsed) meta = 'Already in your bundle';

      const isSelected = Boolean(isCurrentProduct);

      let variantsHtml = '';
      if (isExpanded) {
        const selections = this.pickerOptionSelections[product.id] || this.getSelectionsForVariant(product, null);
        this.pickerOptionSelections[product.id] = selections;

        const optionsHtml = product.options
          .map((option, optionIndex) => {
            const currentVal = selections[option.name];
            const optionsMarkup = option.values
              .map((value) => {
                const available = this.optionValueAvailable(product, optionIndex, value, selections, usedVariantIds, isCurrentProduct ? currentEntry : null);
                const selectedAttr = value === currentVal ? 'selected' : '';
                const disabledAttr = available ? '' : 'disabled';
                return `<option value="${escapeAttr(value)}" ${selectedAttr} ${disabledAttr}>${escapeHtml(value)}${available ? '' : ' — Sold out'}</option>`;
              })
              .join('');

            return `
              <label class="bundle-picker__option">
                <span class="bundle-picker__option-label">${escapeHtml(option.name)}</span>
                <select class="bundle-picker__option-select" data-role="picker-option-select" data-product-id="${product.id}" data-option-index="${optionIndex}" data-option-name="${escapeAttr(option.name)}">
                  ${optionsMarkup}
                </select>
              </label>
            `;
          })
          .join('');

        const matchedVariant = this.matchVariant(product, selections);
        const isDuplicateBlocked =
          matchedVariant && !this.allowDuplicates && usedVariantIds.has(String(matchedVariant.id)) && !(isCurrentProduct && String(currentEntry.variantId) === String(matchedVariant.id));
        const confirmDisabled = !matchedVariant || !matchedVariant.available || isDuplicateBlocked;
        let confirmLabel = 'Select this option';
        if (matchedVariant && isDuplicateBlocked) confirmLabel = 'Already in your bundle';
        else if (!matchedVariant || !matchedVariant.available) confirmLabel = 'Sold out';
        else if (isCurrentProduct && String(currentEntry.variantId) === String(matchedVariant.id)) confirmLabel = 'Selected ✓';

        variantsHtml = `
          <div class="bundle-picker__variants" data-role="picker-variants">
            ${optionsHtml}
            <button
              type="button"
              class="bundle-picker__confirm m-button m-button--primary"
              data-role="picker-confirm"
              data-product-id="${product.id}"
              ${confirmDisabled ? 'disabled' : ''}
            >
              ${confirmLabel}
            </button>
          </div>
        `;
      }

      return `
        <div class="bundle-picker__row${isSelected ? ' is-selected' : ''}" data-role="picker-row">
          <button
            type="button"
            class="bundle-picker__row-main"
            data-role="picker-row-main"
            data-product-id="${product.id}"
            ${!product.available ? 'disabled aria-disabled="true"' : ''}
          >
            <img class="bundle-picker__thumb" src="${escapeAttr(product.image)}" alt="" width="44" height="44" loading="lazy">
            <span class="bundle-picker__row-info">
              <span class="bundle-picker__row-title">${escapeHtml(product.title)}</span>
              ${meta ? `<span class="bundle-picker__row-meta">${escapeHtml(meta)}</span>` : ''}
            </span>
            <span class="bundle-picker__row-price">${escapeHtml(priceLabel)}</span>
            <span class="bundle-picker__row-arrow" aria-hidden="true">${isExpanded ? '&#9652;' : '&#9662;'}</span>
          </button>
          ${variantsHtml}
        </div>
      `;
    }

    onPickerRowClick(rowMainEl) {
      const productId = rowMainEl.dataset.productId;
      const product = this.products.find((p) => String(p.id) === String(productId));
      if (!product || this.activeSlotIndex == null) return;

      if (product.hasOnlyDefaultVariant) {
        const variant = product.variants[0];
        if (!variant) return;
        this.assignSlot(this.activeSlotIndex, product, variant);
        return;
      }

      this.pickerExpandedProductId = this.pickerExpandedProductId === String(product.id) ? null : String(product.id);
      if (this.pickerExpandedProductId && !this.pickerOptionSelections[product.id]) {
        const currentEntry = this.slots[this.activeSlotIndex];
        const prefillVariantId = currentEntry && String(currentEntry.productId) === String(product.id) ? currentEntry.variantId : null;
        this.pickerOptionSelections[product.id] = this.getSelectionsForVariant(product, prefillVariantId);
      }
      this.renderPickerList();
    }

    onPickerOptionChange(selectEl) {
      const productId = selectEl.dataset.productId;
      const optionName = selectEl.dataset.optionName;
      if (!this.pickerOptionSelections[productId]) this.pickerOptionSelections[productId] = {};
      this.pickerOptionSelections[productId][optionName] = selectEl.value;
      this.renderPickerList();
    }

    onPickerConfirm(buttonEl) {
      if (buttonEl.disabled) return;
      const productId = buttonEl.dataset.productId;
      const product = this.products.find((p) => String(p.id) === String(productId));
      if (!product || this.activeSlotIndex == null) return;

      const selections = this.pickerOptionSelections[productId] || {};
      const variant = this.matchVariant(product, selections);
      if (!variant) return;

      this.assignSlot(this.activeSlotIndex, product, variant);
    }

    assignSlot(slotIndex, product, variant) {
      this.slots[slotIndex] = {
        productId: String(product.id),
        variantId: String(variant.id),
        title: product.title,
        variantTitle: product.hasOnlyDefaultVariant ? '' : variant.title,
        image: variant.image || product.image || '',
        priceCents: Number(variant.price || 0),
        priceFormatted: variant.priceFormatted || this.formatMoney(Number(variant.price || 0)),
      };

      this.trackEvent('bundle_product_selected', { slotIndex, productId: product.id, variantId: variant.id });
      this.closePicker();
      this.renderSlots();
      this.render();
    }

    removeFromSlot(slotIndex) {
      if (Number.isNaN(slotIndex) || !this.slots[slotIndex]) return;
      const removed = this.slots[slotIndex];
      this.slots[slotIndex] = null;
      this.trackEvent('bundle_product_removed', { slotIndex, productId: removed.productId });
      this.renderSlots();
      this.render();
    }

    // ---------------------------------------------------------------------
    // Variant helpers
    // ---------------------------------------------------------------------

    getOptionValue(variant, optionIndex) {
      return variant['option' + (optionIndex + 1)];
    }

    getSelectionsForVariant(product, variantId) {
      const variant = variantId ? product.variants.find((v) => String(v.id) === String(variantId)) : null;
      const source = variant || product.variants.find((v) => v.available) || product.variants[0];
      const selections = {};
      product.options.forEach((option, index) => {
        selections[option.name] = source ? this.getOptionValue(source, index) : option.values[0];
      });
      return selections;
    }

    matchVariant(product, selections) {
      return product.variants.find((variant) =>
        product.options.every((option, index) => this.getOptionValue(variant, index) === selections[option.name])
      );
    }

    optionValueAvailable(product, optionIndex, value, selections, usedVariantIds, currentEntry) {
      return product.variants.some((variant) => {
        if (this.getOptionValue(variant, optionIndex) !== value) return false;
        if (!variant.available) return false;
        const isCurrentSelection = currentEntry && String(currentEntry.variantId) === String(variant.id);
        if (!this.allowDuplicates && usedVariantIds.has(String(variant.id)) && !isCurrentSelection) return false;

        return product.options.every((option, i) => {
          if (i === optionIndex) return true;
          const otherVal = selections[option.name];
          if (!otherVal) return true;
          return this.getOptionValue(variant, i) === otherVal;
        });
      });
    }

    // ---------------------------------------------------------------------
    // Cart
    // ---------------------------------------------------------------------

    addBundleToCart() {
      const offer = this.offer;
      const filled = this.slots.filter(Boolean);
      if (!offer || filled.length !== offer.quantity) return;

      [this.summaryCta, this.stickyCta].forEach((btn) => {
        if (btn) btn.disabled = true;
      });

      const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const bundleLabel = `${offer.quantity}-Piece Bundle — ${offer.discount}% OFF${
        offer.freeShipping ? ' + Free Shipping' : ''
      }`;

      const items = filled.map((entry) => ({
        id: Number(entry.variantId),
        quantity: 1,
        properties: {
          Bundle: bundleLabel,
          _bundle_id: bundleId,
        },
      }));

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const payload = {
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      };

      const { MinimogSettings, MinimogStrings, MinimogTheme, MinimogEvents } = window;

      fetch(MinimogSettings.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showMessage(response.description || 'Something went wrong adding your bundle to the cart.');
            document.dispatchEvent(
              new CustomEvent('product-ajax:error', { detail: { errorMessage: response.description } })
            );
            return;
          }

          const cartDrawer = document.querySelector('m-cart-drawer');

          if (MinimogSettings.use_ajax_atc) {
            if (cartDrawer && MinimogSettings.enable_cart_drawer) {
              MinimogTheme.Notification.show({
                target: cartDrawer.querySelector('m-cart-items'),
                method: 'prepend',
                type: 'success',
                message: (MinimogStrings && MinimogStrings.itemAdded) || 'Bundle added to cart!',
                delay: 400,
              });
              cartDrawer.open();
            } else {
              MinimogTheme.Notification.show({
                target: document.body,
                method: 'appendChild',
                type: 'success',
                message: (MinimogStrings && MinimogStrings.itemAdded) || 'Bundle added to cart!',
                last: 3000,
                sticky: true,
              });
            }

            MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: response });
            document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: response } }));
          } else {
            window.location = MinimogSettings.routes.cart;
            return;
          }

          this.trackEvent('bundle_add_to_cart', {
            quantity: offer.quantity,
            discount: offer.discount,
            freeShipping: offer.freeShipping,
            bundleId,
          });

          this.slots = new Array(offer.quantity).fill(null);
          this.renderSlots();
          this.render();
        })
        .catch((error) => {
          console.error(error);
          this.showMessage('Something went wrong adding your bundle to the cart. Please try again.');
        })
        .finally(() => {
          [this.summaryCta, this.stickyCta].forEach((btn) => {
            if (btn) btn.disabled = this.slots.filter(Boolean).length !== offer.quantity;
          });
        });
    }

    showMessage(message) {
      if (!this.messageBox) return;
      this.messageBox.textContent = message;
      this.messageBox.hidden = false;
      clearTimeout(this.messageTimeout);
      this.messageTimeout = setTimeout(() => {
        this.messageBox.hidden = true;
      }, 4000);
    }

    trackEvent(name, payload) {
      document.dispatchEvent(
        new CustomEvent(`bundle:${name}`, { bubbles: true, detail: payload || {} })
      );
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat;
      if (typeof window.formatMoney === 'function' && format) {
        try {
          return window.formatMoney(cents, format);
        } catch (error) {
          // fall through to Intl fallback below
        }
      }
      const currency = this.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        }).format(cents / 100);
      } catch (error) {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    }

    // ---------------------------------------------------------------------
    // Progress / summary render
    // ---------------------------------------------------------------------

    render() {
      const offer = this.offer;
      const filled = this.slots.filter(Boolean);
      const count = filled.length;
      const required = offer ? offer.quantity : 0;
      const complete = offer != null && count === required && required > 0;

      if (this.progressText) {
        this.progressText.textContent = offer ? `${count} / ${required} selected` : '';
      }
      if (this.progressBarFill) {
        const percent = required > 0 ? Math.min(100, (count / required) * 100) : 0;
        this.progressBarFill.style.width = `${percent}%`;
      }

      if (this.summaryList) {
        this.summaryList.innerHTML = this.slots
          .map((entry, index) => {
            if (entry) {
              return `
                <li class="bundle-summary__row is-filled">
                  <span class="bundle-summary__mark" aria-hidden="true">&#10003;</span>
                  <span class="bundle-summary__title">${escapeHtml(entry.title)}${entry.variantTitle ? ` — ${escapeHtml(entry.variantTitle)}` : ''}</span>
                  <button type="button" class="bundle-summary__remove" data-role="remove-product" data-slot-index="${index}" aria-label="Remove ${escapeAttr(entry.title)}">&times;</button>
                </li>
              `;
            }
            return `
              <li class="bundle-summary__row is-empty">
                <span class="bundle-summary__mark" aria-hidden="true">&#9675;</span>
                <span class="bundle-summary__title">Product ${index + 1}</span>
              </li>
            `;
          })
          .join('');
      }

      const subtotalCents = filled.reduce((sum, entry) => sum + entry.priceCents, 0);
      const discountCents = offer ? Math.round((subtotalCents * offer.discount) / 100) : 0;
      const finalCents = subtotalCents - discountCents;

      if (this.summarySubtotal) this.summarySubtotal.textContent = this.formatMoney(subtotalCents);
      if (this.summaryDiscount) {
        this.summaryDiscount.textContent = offer ? `-${this.formatMoney(discountCents)} (${offer.discount}%)` : '—';
      }
      if (this.summaryTotal) this.summaryTotal.textContent = this.formatMoney(finalCents);
      if (this.summaryShipping) {
        this.summaryShipping.textContent = offer && offer.freeShipping ? 'Free shipping' : 'Calculated at checkout';
      }
      if (this.summaryCount) this.summaryCount.textContent = offer ? `${count} / ${required} selected` : '';

      [this.summaryCta, this.stickyCta].forEach((btn) => {
        if (btn) btn.disabled = !complete;
      });

      if (this.stickyBar) this.stickyBar.hidden = offer == null;
      if (this.stickyProgressText) {
        this.stickyProgressText.textContent = offer
          ? complete
            ? 'Add Bundle to Cart'
            : `${count} / ${required} selected — Choose ${required - count} more`
          : '';
      }

      if (complete) {
        this.trackEvent('bundle_completed', { quantity: required, discount: offer.discount });
      }
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => {
      switch (char) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        default:
          return '&#39;';
      }
    });
  }

  function escapeAttr(value) {
    return escapeHtml(value);
  }

  customElements.define('m-bundle-builder', MBundleBuilder);
}
